import { Button, Flex, Text } from "@mantine/core";
import { type JSX, useState } from "react";
import { useAnalytics, withPolymorphicClickAnalytics } from "@/analytics";
import type { IntegratedSalesSteps } from "@/sales-tools/home-product/components/types";
import { usePartner, useSessionId, useTypedMostRecentTask } from "@/sdk";
import { postDeclination } from "../../api";
import type { EligibleProduct } from "../../stores";
import { useProductOfferStore } from "../../stores";
import { useIntegratedFlowStore } from "../../stores/useIntegratedFlowStore";
import { SaleProgress } from "../../types";
import { RightArrowIcon } from "../Icons";
import styles from "./SalesFooter.module.css";

interface ContinueButtonProps {
    text?: string;
    onClick?: () => Promise<void>;
    nextStep?: IntegratedSalesSteps;
    isDisabled?: boolean;
}

interface EnrollSectionProps {
    product?: EligibleProduct;
    onBack?: () => void;
    continueButtonProps?: ContinueButtonProps;
}

export function SalesFooter({
    product,
    onBack,
    continueButtonProps: {
        text = "Continue",
        nextStep = product?.isProductAsurionBilled ? "CollectCustomerInfoStep" : "SendLinkStep",
        isDisabled = false,
        onClick,
    } = {},
}: EnrollSectionProps): JSX.Element {
    const AnalyticsButton = withPolymorphicClickAnalytics(Button<"button">, "ButtonClick");
    const [declining, setDeclining] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const { isVoice, isChat } = useTypedMostRecentTask();

    if (!isVoice && !isChat) {
        throw new Error("Active task is not voice or chat task");
    }

    const sessionId = useSessionId();
    const updateProduct = useProductOfferStore((state) => state.updateProduct);
    const updateSelectedProduct = useProductOfferStore((state) => state.updateSelectedProduct);
    const integratedStep = useIntegratedFlowStore((state) => state.getCurrentStep(sessionId));
    const updateIntegratedStep = useIntegratedFlowStore((state) => state.updateIntegratedStep);

    const { dispatcher } = useAnalytics();
    const partner = usePartner();

    const decline = async () => {
        try {
            if (product) {
                await postDeclination({
                    sessionId,
                    partner,
                    productOfferId: product.id,
                    productSku: product.productSku,
                });
                updateProduct(sessionId, product.id, { saleProgress: SaleProgress.Declined });
            }
            setDeclining(false);
        } catch {
            setDeclining(false);
        }
    };

    const onContinue = async () => {
        void dispatcher
            .withExtra({ nextStep: integratedStep })
            .dispatchBusinessEvent("ButtonClicked_ContinueToDisclosures");
        if (onClick) {
            setIsLoading(true);
            try {
                await onClick?.();
            } catch {
                /* do nothing */
            } finally {
                setIsLoading(false);
            }
        } else {
            updateSelectedProduct(sessionId, product);
            updateIntegratedStep(sessionId, nextStep);
        }
    };

    return (
        <Flex className={styles.footerBox}>
            {onBack && (
                <Flex>
                    <Button
                        onClick={onBack}
                        size="xs"
                        variant="transparent"
                        color="dark.1"
                        fz="xs"
                        leftSection={<RightArrowIcon height="12" width="12" x="-1" />}
                    >
                        Back
                    </Button>
                </Flex>
            )}
            <Flex w="100%" justify="end">
                <AnalyticsButton
                    analytics={{
                        actionId: "EnrollmentDeclineButton",
                        eventData: {
                            nextStep: integratedStep,
                        },
                    }}
                    color="white"
                    data-testid="enrollment-decline-button"
                    size="xs"
                    loading={declining}
                    disabled={!product || product.saleProgress !== SaleProgress.InProgress}
                    onClick={() => {
                        setDeclining(true);
                        void decline();
                    }}
                    variant="transparent"
                >
                    <Text td="underline" size="xs">
                        Decline
                    </Text>
                </AnalyticsButton>
                <Button
                    disabled={declining || !product || isDisabled}
                    className={styles.actionText}
                    color={isChat ? "primary.6" : "white"}
                    fz="xs"
                    size="xs"
                    variant={isChat ? undefined : "outline"}
                    data-testid="continue-to-disclosures"
                    loading={isLoading}
                    onClick={() => void onContinue()}
                >
                    {text}
                </Button>
            </Flex>
        </Flex>
    );
}
